/**
 * ExposureOverviewPage — Admin Credit Exposure Dashboard
 *
 * Calls:
 *   GET /credit/applications  → all credit cases
 *   GET /credit/limits        → sanctioned limits
 *
 * Calculates:
 *   - Total sanctioned
 *   - Total disbursed
 *   - Outstanding exposure
 *   - Defaulted accounts
 *
 * Renders dashboard cards + horizontal bar charts.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Refresh,
  AccountBalance,
  Warning,
  CheckCircle,
  Loop,
  ErrorOutline,
} from '@mui/icons-material';
import { creditAPI } from 'services/api';

// ─── Helpers ────────────────────────────────────────────────
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  const n = parseFloat(amount);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

// ─── Horizontal Bar Chart ────────────────────────────────────
const HBar = ({ label, value, maxValue, color, suffix = '' }) => {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-600 truncate max-w-[60%]">{label}</span>
        <span className="font-semibold text-gray-800">
          {typeof value === 'number' && value >= 1000 ? formatCurrency(value) : value}
          {suffix}
        </span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

// ─── Metric Row ──────────────────────────────────────────────
const MetricRow = ({ label, value, subtext, progress, color = '#6366f1' }) => (
  <div className="py-3 border-b border-gray-50 last:border-b-0">
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-bold text-gray-900">{value}</span>
    </div>
    {progress !== undefined && (
      <LinearProgress
        variant="determinate"
        value={Math.min(progress, 100)}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: `${color}20`,
          '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: color },
        }}
      />
    )}
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

// ═════════════════════════════════════════════════════════════
const ExposureOverviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [applications, setApplications] = useState([]);
  const [limits, setLimits] = useState(null);

  // ── Load Data ─────────────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [appsRes, limitsRes] = await Promise.allSettled([
        creditAPI.getApplications(),
        creditAPI.getLimits(),
      ]);

      if (appsRes.status === 'fulfilled') {
        setApplications(appsRes.value.data || []);
      }
      if (limitsRes.status === 'fulfilled') {
        setLimits(limitsRes.value.data || null);
      }
    } catch (err) {
      console.error('ExposureOverview load error:', err);
      setError('Failed to load exposure data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived Metrics ────────────────────────────────────────
  const metrics = useMemo(() => {
    const apps = applications;
    const applied = apps.filter((a) => ['applied', 'pending'].includes((a.status || '').toLowerCase()));
    const approved = apps.filter((a) => (a.status || '').toLowerCase() === 'approved');
    const disbursed = apps.filter((a) => (a.status || '').toLowerCase() === 'disbursed');
    const active = apps.filter((a) => (a.status || '').toLowerCase() === 'active');
    const settled = apps.filter((a) => (a.status || '').toLowerCase() === 'settled');
    const rejected = apps.filter((a) => (a.status || '').toLowerCase() === 'rejected');
    const defaulted = apps.filter((a) => (a.status || '').toLowerCase() === 'defaulted');

    const totalSanctioned = apps.reduce(
      (s, a) => s + (a.sanctioned_amount || a.approved_amount || 0),
      0
    );
    const totalDisbursed = [...disbursed, ...active, ...settled].reduce(
      (s, a) => s + (a.disbursed_amount || a.sanctioned_amount || 0),
      0
    );
    const outstanding = active.reduce(
      (s, a) => s + (a.outstanding_amount || a.disbursed_amount || a.sanctioned_amount || 0),
      0
    );
    const defaultedAmount = defaulted.reduce(
      (s, a) => s + (a.outstanding_amount || a.disbursed_amount || a.sanctioned_amount || 0),
      0
    );

    const activeAndDisbursed = disbursed.length + active.length;
    const npaRisk =
      activeAndDisbursed > 0
        ? (defaulted.length / (activeAndDisbursed + defaulted.length)) * 100
        : 0;

    const disbursedPct = totalSanctioned > 0 ? (totalDisbursed / totalSanctioned) * 100 : 0;
    const outstandingPct = totalDisbursed > 0 ? (outstanding / totalDisbursed) * 100 : 0;

    // Pipeline
    const pipeline = {
      applied: applied.length,
      approved: approved.length,
      disbursed: disbursed.length,
      active: active.length,
      settled: settled.length,
      rejected: rejected.length,
      defaulted: defaulted.length,
    };

    // District-level aggregation
    const districtMap = {};
    apps.forEach((a) => {
      const d = a.district || a.location || 'Unknown';
      if (!districtMap[d]) districtMap[d] = { exposure: 0, count: 0 };
      districtMap[d].exposure += a.sanctioned_amount || a.approved_amount || 0;
      districtMap[d].count += 1;
    });
    const districtExposure = Object.entries(districtMap)
      .map(([district, data]) => ({ district, ...data }))
      .sort((a, b) => b.exposure - a.exposure)
      .slice(0, 8);

    return {
      totalSanctioned,
      totalDisbursed,
      outstanding,
      defaultedAmount,
      defaultedCount: defaulted.length,
      npaRisk,
      disbursedPct,
      outstandingPct,
      totalApps: apps.length,
      activeCycles: active.length + disbursed.length,
      settledCycles: settled.length,
      pipeline,
      districtExposure,
    };
  }, [applications]);

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <TrendingUp sx={{ fontSize: 22, color: '#6366f1' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Exposure Overview</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#6366f1' }} />
          <span className="ml-3 text-sm text-gray-500">Loading exposure data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <TrendingUp sx={{ fontSize: 22, color: '#6366f1' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Exposure Overview</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Total credit exposure, NPA risk indicators, and portfolio breakdown
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => loadData(true)}
            disabled={refreshing}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button size="small" onClick={() => loadData(true)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          icon={<AccountBalance />}
          label="Total Sanctioned"
          value={formatCurrency(metrics.totalSanctioned)}
          subtitle={`${metrics.totalApps} applications`}
          color="#6366f1"
        />
        <KPICard
          icon={<TrendingUp />}
          label="Total Disbursed"
          value={formatCurrency(metrics.totalDisbursed)}
          subtitle={`${metrics.disbursedPct.toFixed(1)}% utilization`}
          color="#0ea5e9"
        />
        <KPICard
          icon={<Loop />}
          label="Outstanding Exposure"
          value={formatCurrency(metrics.outstanding)}
          subtitle={`${metrics.activeCycles} active cycles`}
          color="#f59e0b"
        />
        <KPICard
          icon={<ErrorOutline />}
          label="Defaulted"
          value={formatCurrency(metrics.defaultedAmount)}
          subtitle={`${metrics.defaultedCount} account${metrics.defaultedCount !== 1 ? 's' : ''}`}
          color={metrics.defaultedCount > 0 ? '#ef4444' : '#10b981'}
          alert={metrics.defaultedCount > 0}
        />
      </div>

      {/* ── Two-column grid: Exposure Radar + Pipeline ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Exposure Radar */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-gray-700">Exposure Breakdown</h3>
            {metrics.npaRisk > 10 && (
              <Chip
                icon={<Warning sx={{ fontSize: 14 }} />}
                label={`NPA Risk: ${metrics.npaRisk.toFixed(1)}%`}
                color="error"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
              />
            )}
          </div>
          <div className="space-y-0">
            <MetricRow
              label="Total Sanctioned"
              value={formatCurrency(metrics.totalSanctioned)}
              progress={100}
              color="#6366f1"
              subtext="Approved credit limit across all applications"
            />
            <MetricRow
              label="Total Disbursed"
              value={formatCurrency(metrics.totalDisbursed)}
              progress={metrics.disbursedPct}
              color="#0ea5e9"
              subtext={`${metrics.disbursedPct.toFixed(1)}% of sanctioned amount`}
            />
            <MetricRow
              label="Outstanding"
              value={formatCurrency(metrics.outstanding)}
              progress={metrics.outstandingPct}
              color="#f59e0b"
              subtext={`${metrics.outstandingPct.toFixed(1)}% of disbursed amount`}
            />
            <MetricRow
              label="NPA Risk"
              value={`${metrics.npaRisk.toFixed(1)}%`}
              progress={metrics.npaRisk}
              color={metrics.npaRisk > 10 ? '#ef4444' : metrics.npaRisk > 5 ? '#f59e0b' : '#10b981'}
              subtext={
                metrics.npaRisk > 10
                  ? 'High risk — immediate attention required'
                  : metrics.npaRisk > 5
                    ? 'Moderate risk — monitor closely'
                    : 'Healthy portfolio'
              }
            />
          </div>
        </div>

        {/* Credit Pipeline */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-gray-700">Credit Pipeline</h3>
            <Chip
              label={`${metrics.totalApps} total`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
            />
          </div>
          <div className="space-y-1">
            {[
              { label: 'Applied / Pending', count: metrics.pipeline.applied, color: '#6366f1' },
              { label: 'Approved', count: metrics.pipeline.approved, color: '#0ea5e9' },
              { label: 'Disbursed', count: metrics.pipeline.disbursed, color: '#10b981' },
              { label: 'Active', count: metrics.pipeline.active, color: '#f59e0b' },
              { label: 'Settled', count: metrics.pipeline.settled, color: '#8b5cf6' },
              { label: 'Rejected', count: metrics.pipeline.rejected, color: '#ef4444' },
              { label: 'Defaulted', count: metrics.pipeline.defaulted, color: '#dc2626' },
            ].map((s) => (
              <HBar
                key={s.label}
                label={s.label}
                value={s.count}
                maxValue={Math.max(metrics.totalApps, 1)}
                color={s.color}
              />
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
            <div className="text-xs">
              <span className="text-gray-400">Approval Rate</span>
              <p className="font-semibold text-gray-700">
                {metrics.pipeline.applied > 0
                  ? `${((metrics.pipeline.approved / (metrics.pipeline.applied + metrics.pipeline.approved)) * 100).toFixed(1)}%`
                  : 'N/A'}
              </p>
            </div>
            <div className="text-xs">
              <span className="text-gray-400">Settlement Rate</span>
              <p className="font-semibold text-gray-700">
                {metrics.pipeline.active > 0 || metrics.pipeline.settled > 0
                  ? `${((metrics.pipeline.settled / (metrics.pipeline.active + metrics.pipeline.settled)) * 100).toFixed(1)}%`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── District Exposure ── */}
      {metrics.districtExposure.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">District-Level Exposure</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
            {metrics.districtExposure.map((d, i) => (
              <HBar
                key={d.district}
                label={`${d.district} (${d.count} app${d.count !== 1 ? 's' : ''})`}
                value={d.exposure}
                maxValue={Math.max(...metrics.districtExposure.map((x) => x.exposure), 1)}
                color={['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'][i % 8]}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Applications Table (compact) ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">All Applications ({applications.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farmer</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">District</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sanctioned</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Disbursed</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Outstanding</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {applications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-400 text-sm">
                    No credit applications found
                  </td>
                </tr>
              ) : (
                applications.slice(0, 100).map((app) => {
                  const statusColors = {
                    approved: 'success',
                    disbursed: 'primary',
                    active: 'success',
                    settled: 'default',
                    rejected: 'error',
                    defaulted: 'error',
                    pending: 'warning',
                    applied: 'info',
                  };
                  const st = (app.status || '').toLowerCase();
                  return (
                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">#{app.id}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {app.farmer_name || app.user_name || `Farmer #${app.user_id || app.farmer_id || '—'}`}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {app.district || app.location || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {formatCurrency(app.sanctioned_amount || app.approved_amount || 0)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatCurrency(app.disbursed_amount || 0)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatCurrency(app.outstanding_amount || 0)}
                      </td>
                      <td className="px-4 py-3">
                        <Chip
                          label={(app.status || 'unknown').toUpperCase()}
                          size="small"
                          variant="outlined"
                          color={statusColors[st] || 'default'}
                          sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── KPI Card ────────────────────────────────────────────────
const KPICard = ({ icon, label, value, subtitle, color, alert = false }) => (
  <div
    className={`rounded-2xl border bg-white p-5 hover:shadow-lg transition-all duration-300 group ${
      alert ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
    }`}
  >
    <div className="flex items-start justify-between mb-3">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${color}15` }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
      </div>
      {alert && (
        <Chip
          icon={<Warning sx={{ fontSize: 12 }} />}
          label="Alert"
          color="error"
          size="small"
          sx={{ fontWeight: 700, fontSize: '0.55rem', height: 20 }}
        />
      )}
    </div>
    <p className="text-2xl font-bold text-gray-900 mb-0.5">{value}</p>
    <p className="text-sm font-medium text-gray-600">{label}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

export default ExposureOverviewPage;
